// CustomerMaster.jsx
import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaTrashAlt,
  FaPlus,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaSearch,
  FaDownload,
  FaBan,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import dayjs from "dayjs";
import CustomerFormModal from "./CustomerFormModal";
import CustomerViewModal from "./CustomerViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "./useScreenPermissions";
import { useNavigate } from "react-router-dom";

const API_URL = "/api";

const CustomerMaster = () => {
  // data + ui state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("All");
  const [status, setStatus] = useState("All");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const navigate = useNavigate();
  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // --- THEME CSS VARIABLES FOR SIDEBAR & GLOBAL THEME ---
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85", // primary accent
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#9aa7b0", // match vendor's slightly darker inactive
      "--search-icon": "#cbd5da",
      // status colors
      
      // page and header
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
      // surfaces/dividers
      "--surface-1": "#ffffff",
      "--surface-2": "#f6fbfa",
      "--divider": "#e6efee",
      "--danger": "#ef4444",
    };

    Object.entries(themeVars).forEach(([key, value]) =>
      document.documentElement.style.setProperty(key, value)
    );

    // inject semantic CSS (same as vendor)
    const styleId = "customer-master-theme-overrides";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .primary-btn {
        

        .status-active {
          
          
         
          .cust-status{
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

          .cust-status-in{
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

         

        
        /* center table content */
        table { margin: 0 auto; }
        th, td { text-align: center !important; vertical-align: middle !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);
  // -------------------------------------------------------

  useEffect(() => {
    fetchCustomers();
  }, []);

 // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // fetch list
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/customers`);
      const data = await res.json();
      setCustomers(data);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setCustomers([]);
    }
    setLoading(false);
  };

  // sorting (keeps existing logic but UI arrows consistent)

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    
    const sortedCustomers = [...customers].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === "created_at") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === "customer_code") {
        const aNum = parseInt(a[key].split("-")[1]);
        const bNum = parseInt(b[key].split("-")[1]);
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      
      // Handle string fields
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    
    setCustomers(sortedCustomers);
  };

  // Vendor-style renderSortArrows to match exactly
  const renderSortArrows = (col) => {
    const active = sortConfig.key === col;
    return (
      <span className="table-header-sort" aria-hidden>
        <FaSortUp className={`${active && sortConfig.direction === 'asc' ? 'sort-active' : 'sort-inactive'}`} />
        <FaSortDown className={`${active && sortConfig.direction === 'desc' ? 'sort-active' : 'sort-inactive'}`} />
      </span>
    );
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setShowView(true);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDelete = async (customer) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      const response = await fetch(`${API_URL}/customers/${customer.customer_code}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete customer");
      }
      fetchCustomers();
    } catch (error) {
      console.error("Error deleting customer:", error);
      alert(error.message || "An error occurred while deleting the customer");
    }
  };

 const handleDeactivate = async (customer) => {
    if (!window.confirm("Are you sure you want to deactivate this customer?"))
      return;
    try {
      console.log("Deactivating customer:", customer.customer_code);
      const response = await fetch(
        `${API_URL}/customers/${customer.customer_code}/deactivate`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        }
      );
      console.log("Deactivate response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Deactivate error:", errorData);
        throw new Error(errorData.message || "Failed to deactivate customer");
      }
      // Success message
      const result = await response.json();
      console.log("Deactivate result:", result);
      // Refresh customer list
      fetchCustomers();
    } catch (error) {
      console.error("Error deactivating customer:", error);
      alert(error.message || "An error occurred while deactivating the customer");
    }
  };

  // Reactivate function
  const handleReactivate = async (customer) => {
    if (!window.confirm("Are you sure you want to reactivate this customer?"))
      return;
    try {
      console.log("Reactivating customer:", customer.customer_code);
      const response = await fetch(
        `${API_URL}/customers/${customer.customer_code}/reactivate`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        }
      );
      console.log("Reactivate response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Reactivate error:", errorData);
        throw new Error(errorData.message || "Failed to reactivate customer");
      }
      // Success message
      const result = await response.json();
      console.log("Reactivate result:", result);
      // Refresh customer list
      fetchCustomers();
    } catch (error) {
      console.error("Error reactivating customer:", error);
      alert(error.message || "An error occurred while reactivating the customer");
    }
  };

   const handleCreate = () => {
    setSelectedCustomer(null);
    setIsEdit(false);
    setShowForm(true);
  };

    const handleLogout = () => {
    navigate('/login');
  };

  const handleDownloadCSV = () => {
    const headers = [
      "customer_code",
      "customer_name",
      "customer_type",
      "contact_name",
      "contact_email",
      "contact_phone",
      "country",
      "status",
      "created_at",
    ];
        const rows = customers.map(c => [
      c.customer_code,
      c.customer_name,
      c.customer_type,
      (c.contact_first_name && c.contact_last_name) ? `${c.contact_first_name} ${c.contact_last_name}` : '',
      c.contact_email || '',
      c.contact_phone || '',
      c.country,
      c.is_active ? "Active" : "Inactive",
      dayjs(c.created_at).format("YYYY-MM-DD HH:mm"),
    ]);
   
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFormSubmit = async (formData) => {
    setShowForm(false);
    fetchCustomers();
  };

  // filtering
 const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      searchTerm === "" ||
      customer.customer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.contact_email && customer.contact_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.contact_phone && customer.contact_phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.contact_first_name && customer.contact_first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.contact_last_name && customer.contact_last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      customer.country.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesCategory =
      category === "All" || customer.customer_type === category;
      
    const matchesStatus =
      status === "All" ||
      (status === "Active" && customer.is_active) ||
      (status === "Inactive" && !customer.is_active);
      
    return matchesSearch && matchesCategory && matchesStatus;
  });


  // pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const customersForPage = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    buttons.push(
      <button key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}>
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      const isActive = i === currentPage;
      buttons.push(
        <button key={i} onClick={() => handlePageChange(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${isActive ? 'bg-teal-600 text-white page-active' : 'page-inactive'}`}>
          {i}
        </button>
      );
    }

    buttons.push(
      <button key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${currentPage === totalPages || totalPages === 0 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}>
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  // permission guards
  const canCreate = hasFullAccess || hasPermission('CUSTOMER_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('CUSTOMER_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('CUSTOMER_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('CUSTOMER_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: "var(--page-bg)" }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4" style={{ borderColor: "var(--accent-color)" }}></div>
          <p className="mt-4 text-lg font-medium" style={{ color: "var(--text-muted)" }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Sidebar theme to match Create button color (active item / profile / logout)
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    // keys Sidebar reads for coloring active/profile/logout
    activeBg: 'var(--accent-color, #2b8f85)',   // active item background = same as Create button
    profileBg: 'var(--accent-color, #2b8f85)',  // profile/logout background
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  // ---------- render ----------
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--page-bg)" }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} onLogout={handleLogout} />

      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">Customer Master</h1>
            <p className="mt-1 text-base text-gray-600">Manage your customer relationships and information</p>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={handleDownloadCSV} className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium transition-all duration-200 shadow-sm rounded-none">
              <FaDownload className="h-4 w-4" />
              Export 
            </button>

            {canCreate && (
              <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm rounded-none bg-teal-600 text-white">
                <FaPlus className="h-4 w-4" />
                Create Customer
              </button>
            )}
          </div>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 text-blue-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Active Customers</p>
                <p className="text-2xl font-bold text-gray-900">{customers.filter(c => c.is_active).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Inactive Customers</p>
                <p className="text-2xl font-bold text-gray-900">{customers.filter(c => !c.is_active).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 text-amber-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                   customers.filter((c) => {
                      const createdDate = new Date(c.created_at);
                      const now = new Date();
                      return (
                        createdDate.getMonth() === now.getMonth() &&
                        createdDate.getFullYear() === now.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-purple-100 text-purple-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Filter */}
        <section className="p-5 bg-white border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search Customers</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by code, name, contact, country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200"
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

            <button className="px-5 py-2 primary-btn font-medium bg-teal-600 text-white hover:shadow transition-all duration-200 shadow-sm rounded-none" onClick={() => setCurrentPage(1)}>
              Search
            </button>
          </div>
        </section>

        {/* Table */}
        <section className="bg-white border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-gray-900">Customer List</h2>
            <div className="flex gap-2">{renderPagination()}</div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: 'var(--table-header-bg)' }} className="text-gray-600 text-xs font-semibold uppercase">
                <tr>
                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('customer_code')}>
                    <div className="table-header-cell" style={{ gap: '2px' }}>
                      <span className="table-header-label">Customer Code</span>
                      <span className="table-header-arrow-box">{renderSortArrows('customer_code')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('customer_name')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Customer Name</span>
                      <span className="table-header-arrow-box">{renderSortArrows('customer_name')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('customer_type')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Customer Type</span>
                      <span className="table-header-arrow-box">{renderSortArrows('customer_type')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200">
                    <div className="table-header-cell">
                      <span className="table-header-label">Contact Name</span>
                      <span className="table-header-arrow-box">{/* no arrows for this column */}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('contact_email')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Contact Email</span>
                      <span className="table-header-arrow-box">{renderSortArrows('contact_email')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('contact_phone')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Contact Phone</span>
                      <span className="table-header-arrow-box">{renderSortArrows('contact_phone')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('country')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Country</span>
                      <span className="table-header-arrow-box">{renderSortArrows('country')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('is_active')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Status</span>
                      <span className="table-header-arrow-box">{renderSortArrows('is_active')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200" onClick={() => handleSort('created_at')}>
                    <div className="table-header-cell">
                      <span className="table-header-label">Created at</span>
                      <span className="table-header-arrow-box">{renderSortArrows('created_at')}</span>
                    </div>
                  </th>

                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-300">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-gray-500">Loading customers...</td>
                  </tr>
                ) : customersForPage.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-gray-500">No customers found.</td>
                  </tr>
                ) : (
                  customersForPage.map((customer) => (
                    <tr key={customer.customer_code} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap">
                        <p className="cursor-pointer" onClick={() => canView && handleView(customer)}>{customer.customer_code}</p>
                      </td>
                      <td className="px-6 py-3 text-gray-600">{customer.customer_name}</td>
                      <td className="px-6 py-3 text-gray-600">{customer.customer_type}</td>
                      <td className="px-6 py-3 text-gray-600">{(customer.contact_first_name || customer.contact_last_name) ? `${customer.contact_first_name || ''} ${customer.contact_last_name || ''}`.trim() : '-'}</td>
                      <td className="px-6 py-3 break-all max-w-[220px] text-gray-600">{customer.contact_email || '-'}</td>
                      <td className="px-6 py-3 break-all max-w-[180px] text-gray-600">{customer.contact_phone || '-'}</td>
                      <td className="px-6 py-3 text-gray-600">{customer.country}</td>
                      <td className="px-6 py-3">
                        <span onClick={() => customer.is_active ? handleDeactivate(customer) : handleReactivate(customer)}
                         className= {`  rounded-full ${customer.is_active ? 'cust-status font-bold bg-teal-50 text-teal-700 hover:bg-teal-100' : 'cust-status-in font-bold bg-amber-50 text-amber-700 hover:bg-amber-100 '}`}>
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {customer.created_at ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900">{dayjs(customer.created_at).format("DD MMM YYYY")}</span>
                            <span className="text-xs text-gray-600">{dayjs(customer.created_at).format("HH:mm:ss")}</span>
                          </div>
                        ) : "-"}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-3 justify-center">
                          {canView && (
                            <button onClick={() => handleView(customer)} title="View Details" className="text-blue-600 hover:text-blue-800 transition-colors duration-200">
                              <FaEye className="h-4 w-4" />
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => handleEdit(customer)} title="Edit Customer" className="text-green-600 hover:text-green-800 transition-colors duration-200">
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            customer.is_active ? (
                              <button title="Deactivate" className="text-red-600 hover:text-red-800 transition-colors duration-200" onClick={() => handleDeactivate(customer)}>
                                <FaBan className="h-4 w-4" />
                              </button>
                            ) : (
                              <button title="Reactivate" className="text-blue-600 hover:text-blue-800 transition-colors duration-200" onClick={() => handleReactivate(customer)}>
                                <FaCheck className="h-4 w-4" />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

         {/* Modals */}
        {showForm && (
          <CustomerFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={handleFormSubmit}
            initialData={selectedCustomer}
            isEdit={isEdit}
            loading={formLoading}
          />
        )}
        {showView && selectedCustomer && (
          <CustomerViewModal
            open={showView}
            onClose={() => setShowView(false)}
            customer={selectedCustomer}
          />
        )}
      </main>
    </div>
  );
};

export default CustomerMaster;
